// src/pages/Onboarding.js - Con Visual Theme del DomainContext applicato
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Grid,
  Avatar,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDomain, useVisualTheme, useBranding, useLocalization } from '../context/DomainContext';
import { BACKEND_URL } from '../config';

const Onboarding = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    displayName: '',
    ageGroup: '',
    language: 'it-IT',
    selectedAgentId: '',
    preferences: {}
  });
  
  // Stato per la gestione degli agenti
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentsError, setAgentsError] = useState('');
  
  const navigate = useNavigate();
  // ✅ AGGIUNTO updateUserProfile dal contesto Auth
  const { user, completeOnboarding, updateUserProfile } = useAuth();
  
  // 🎨 Hook per Visual Theme
  const visualTheme = useVisualTheme();
  const { brandName, logoUrl, organizationName } = useBranding();
  const { getText } = useLocalization();
  const { config, loading: domainLoading } = useDomain();

  // Verifica autenticazione
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // Steps dell'onboarding (aggiornato per includere la selezione dell'agente)
  const steps = [
    getText('onboarding_step_1_title', 'Benvenuto'),
    'Configurazione Profilo',
    'Preferenze',
    'Selezione Agente',
    'Completamento'
  ];

  const ageGroups = [
    { value: '18-25', label: '18-25 anni' },
    { value: '26-35', label: '26-35 anni' },
    { value: '36-45', label: '36-45 anni' },
    { value: '46-55', label: '46-55 anni' },
    { value: '56+', label: '56+ anni' },
    { value: 'prefer_not_say', label: 'Preferisco non dirlo' }
  ];

  // NUOVA FUNZIONE: Carica agenti disponibili
  const fetchAvailableAgents = useCallback(async () => {
    if (!user || !config?.project_id) return;
    
    setLoadingAgents(true);
    setAgentsError('');
    
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/api/agents/available?project_id=${config.project_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Errore ${response.status}: ${response.statusText}`);
      }
      
      const agentsData = await response.json();
      console.log('[Onboarding] Available agents loaded:', agentsData);
      setAvailableAgents(agentsData || []);
      
    } catch (err) {
      console.error('[Onboarding] Error loading agents:', err);
      setAgentsError('Errore nel caricamento degli agenti. Puoi saltare questo step.');
    } finally {
      setLoadingAgents(false);
    }
  }, [user, config?.project_id]);

  // Carica agenti quando si arriva allo step 3
  useEffect(() => {
    if (activeStep === 3) {
      fetchAvailableAgents();
    }
  }, [activeStep, fetchAvailableAgents]);

  const handleNext = () => {
    if (activeStep === 1 && !formData.displayName.trim()) {
      setError('Il nome visualizzato è obbligatorio.');
      return;
    }
    
    if (activeStep === 3 && !formData.selectedAgentId) {
      setError('Seleziona un agente per continuare.');
      return;
    }
    
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleComplete = async () => {
    setLoading(true);
    setError('');

    try {
      const onboardingData = {
        displayName: formData.displayName,
        ageGroup: formData.ageGroup,
        language: formData.language,
        selectedAgentId: formData.selectedAgentId,
        preferences: formData.preferences || {}
      };

      console.log('[Onboarding] Starting completion with data:', onboardingData);
      console.log('[Onboarding] User state:', { 
        email: user?.email, 
        uid: user?.uid,
        hasCompleteOnboarding: !!completeOnboarding,
        hasUpdateUserProfile: !!updateUserProfile
      });

      // Strategia multi-livello per completare l'onboarding
      let success = false;
      let lastError = null;

      // TENTATIVO 1: Usa completeOnboarding se disponibile
      if (completeOnboarding) {
        try {
          console.log('[Onboarding] Trying completeOnboarding...');
          await completeOnboarding(onboardingData);
          success = true;
          console.log('[Onboarding] ✅ completeOnboarding successful');
        } catch (err) {
          console.log('[Onboarding] ❌ completeOnboarding failed:', err.message);
          console.log('[Onboarding] Error details:', err);
          lastError = err;
        }
      }

      // TENTATIVO 2: Usa updateUserProfile se disponibile e primo tentativo fallito
      if (!success && updateUserProfile) {
        try {
          console.log('[Onboarding] Trying updateUserProfile...');
          const payload = {
            user_display_name: onboardingData.displayName,
            user_age_group: onboardingData.ageGroup,
            user_locale: onboardingData.language,
            is_onboarding_completed: true,
            provider_metadata_json: {
              onboarding_completed_at: new Date().toISOString(),
              onboarding_language: onboardingData.language,
              onboarding_preferences: onboardingData.preferences,
              selected_agent_id: onboardingData.selectedAgentId
            }
          };
          console.log('[Onboarding] updateUserProfile payload:', payload);
          
          await updateUserProfile(payload);
          success = true;
          console.log('[Onboarding] ✅ updateUserProfile successful');
        } catch (err) {
          console.log('[Onboarding] ❌ updateUserProfile failed:', err.message);
          console.log('[Onboarding] Error details:', err);
          lastError = err;
        }
      }

      // TENTATIVO 3: Fallback API call diretta
      if (!success) {
        try {
          console.log('[Onboarding] Trying direct API call...');
          const token = await user.getIdToken();
          console.log('[Onboarding] Token obtained, length:', token?.length);
          
          const payload = {
            user_display_name: onboardingData.displayName,
            user_age_group: onboardingData.ageGroup,
            user_locale: onboardingData.language,
            is_onboarding_completed: true,
            provider_metadata_json: {
              onboarding_completed_at: new Date().toISOString(),
              onboarding_language: onboardingData.language,
              onboarding_preferences: onboardingData.preferences,
              selected_agent_id: onboardingData.selectedAgentId
            }
          };
          console.log('[Onboarding] Direct API payload:', payload);
          
          const response = await fetch(`${BACKEND_URL}/api/user/profile`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });

          console.log('[Onboarding] Direct API response status:', response.status);
          
          if (response.ok) {
            const responseData = await response.json();
            console.log('[Onboarding] Direct API response data:', responseData);
            success = true;
            console.log('[Onboarding] ✅ Direct API call successful');
          } else {
            const errorText = await response.text();
            console.error('[Onboarding] ❌ Direct API call failed:', response.status, errorText);
            lastError = new Error(`API Error ${response.status}: ${errorText}`);
          }
        } catch (err) {
          console.log('[Onboarding] ❌ Direct API call failed:', err.message);
          console.log('[Onboarding] Error details:', err);
          lastError = err;
        }
      }

      // GESTIONE AGENTE SELEZIONATO
      if (success && formData.selectedAgentId) {
        try {
          console.log('[Onboarding] Setting up selected agent...');
          const token = await user.getIdToken();
          
          // Aggiungi l'agente al profilo dell'utente
          const agentResponse = await fetch(`${BACKEND_URL}/api/user/agent`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              agent_id: formData.selectedAgentId,
              is_main_agent: true
            })
          });

          if (agentResponse.ok) {
            console.log('[Onboarding] ✅ Agent setup successful');
            
            // Prova a impostare l'agente come main
            try {
              const mainAgentResponse = await fetch(`${BACKEND_URL}/api/user/main-agent`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  agent_id: formData.selectedAgentId
                })
              });
              
              if (mainAgentResponse.ok) {
                console.log('[Onboarding] ✅ Main agent set successfully');
              } else {
                console.warn('[Onboarding] Failed to set agent as main, but continuing...');
              }
            } catch (mainAgentError) {
              console.warn('[Onboarding] Failed to set agent as main, but continuing...');
            }
          } else {
            console.warn('[Onboarding] Failed to add agent, but continuing...');
          }
        } catch (agentError) {
          console.warn('[Onboarding] Error with agent setup:', agentError);
          // Non bloccare l'onboarding per errori dell'agente
        }
      }

      // TENTATIVO 4: Se tutto fallisce, mostra dettagli dell'errore
      if (!success) {
        console.log('[Onboarding] ⚠️ All onboarding update attempts failed');
        console.log('[Onboarding] Last error was:', lastError?.message);
        console.log('[Onboarding] Full error details:', lastError);
        
        // Mostra dettagli dell'errore invece di un messaggio generico
        const errorDetails = lastError?.message || 'Errore sconosciuto';
        setError(`Impossibile completare l'onboarding: ${errorDetails}. Verifica la console per dettagli.`);
        
        // Non navigare automaticamente se c'è un errore
        return;
      }

      // Se tutto è andato bene, naviga alla dashboard
      console.log('[Onboarding] ✅ Onboarding completed successfully');
      navigate('/dashboard', { replace: true });

    } catch (err) {
      console.error('[Onboarding] ❌ Unexpected error during onboarding:', err);
      setError(`Errore imprevisto: ${err.message}. Verifica la console per dettagli.`);
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (domainLoading) {
    return (
      <Container maxWidth="md">
        <Box 
          sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Step Content Components
  const WelcomeStep = () => (
    <Box sx={{ textAlign: 'center' }}>
      {/* 🎨 Logo del brand */}
      {logoUrl && (
        <Box sx={{ mb: 4 }}>
          <img 
            src={logoUrl} 
            alt={`${brandName} Logo`}
            style={{ 
              maxHeight: '120px', 
              maxWidth: '300px', 
              height: 'auto' 
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </Box>
      )}

      <Typography 
        variant="h3" 
        component="h1" 
        gutterBottom
        sx={{ 
          color: visualTheme.text_colors?.primary || '#212529',
          fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
          mb: 3
        }}
      >
        {getText('onboarding_step_1_title', `Benvenuto/a in ${brandName || config.project_name}!`)}
      </Typography>

      <Typography 
        variant="h6" 
        sx={{ 
          color: visualTheme.text_colors?.secondary || '#6c757d',
          mb: 4,
          maxWidth: '600px',
          mx: 'auto',
          lineHeight: 1.6
        }}
      >
        Siamo entusiasti di averti a bordo! Questo breve tour ti aiuterà a configurare il tuo profilo 
        e a scoprire come {brandName || config.project_name} può supportarti nel raggiungere i tuoi obiettivi.
      </Typography>

      {/* 🎨 Card informativa */}
      <Card 
        sx={{ 
          maxWidth: 500, 
          mx: 'auto', 
          mt: 4,
          backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
          borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
        }}
      >
        <CardContent>
          <Typography 
            variant="h6" 
            sx={{ 
              color: visualTheme.secondary_colors?.accent || '#0d6efd',
              mb: 2
            }}
          >
            Cosa ti aspetta:
          </Typography>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Configurazione del tuo profilo personale
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Personalizzazione delle preferenze
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Selezione del tuo agente principale
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Introduzione alle funzionalità principali
            </Typography>
            <Typography variant="body2" sx={{ color: visualTheme.text_colors?.secondary }}>
              • Primo accesso alla dashboard
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const ProfileStep = () => (
    <Box>
      <Typography 
        variant="h5" 
        gutterBottom
        sx={{ 
          color: visualTheme.text_colors?.primary || '#212529',
          fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
          mb: 3
        }}
      >
        Configura il tuo profilo
      </Typography>

      <TextField
        fullWidth
        label={getText('onboarding_step_1_prompt', 'Come preferisci essere chiamato/a?')}
        value={formData.displayName}
        onChange={(e) => updateFormData('displayName', e.target.value)}
        margin="normal"
        required
        placeholder="Es. Mario, Maria, Alex..."
        helperText="Questo sarà il nome che verrà mostrato agli altri utenti"
        sx={{
          mb: 3,
          // 🎨 Styling del campo input
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: visualTheme.secondary_colors?.accent || '#0d6efd',
          }
        }}
      />

      <FormControl 
        fullWidth 
        margin="normal"
        sx={{
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: visualTheme.secondary_colors?.accent || '#0d6efd',
          }
        }}
      >
        <InputLabel>Fascia d'età (opzionale)</InputLabel>
        <Select
          value={formData.ageGroup}
          onChange={(e) => updateFormData('ageGroup', e.target.value)}
          label="Fascia d'età (opzionale)"
        >
          {ageGroups.map((group) => (
            <MenuItem key={group.value} value={group.value}>
              {group.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 🎨 Anteprima dati utente */}
      <Card 
        sx={{ 
          mt: 4, 
          backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
          borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
        }}
      >
        <CardContent>
          <Typography 
            variant="h6" 
            sx={{ 
              color: visualTheme.secondary_colors?.accent || '#0d6efd',
              mb: 2
            }}
          >
            Anteprima del tuo profilo:
          </Typography>
          <Typography variant="body2" sx={{ color: visualTheme.text_colors?.secondary, mb: 1 }}>
            <strong>Email:</strong> {user?.email}
          </Typography>
          <Typography variant="body2" sx={{ color: visualTheme.text_colors?.secondary, mb: 1 }}>
            <strong>Nome:</strong> {formData.displayName || 'Non specificato'}
          </Typography>
          <Typography variant="body2" sx={{ color: visualTheme.text_colors?.secondary }}>
            <strong>Organizzazione:</strong> {organizationName || 'Default'}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  const PreferencesStep = () => (
    <Box>
      <Typography 
        variant="h5" 
        gutterBottom
        sx={{ 
          color: visualTheme.text_colors?.primary || '#212529',
          fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
          mb: 3
        }}
      >
        Personalizza la tua esperienza
      </Typography>

      <FormControl 
        fullWidth 
        margin="normal"
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: visualTheme.secondary_colors?.accent || '#0d6efd',
          }
        }}
      >
        <InputLabel>Lingua preferita</InputLabel>
        <Select
          value={formData.language}
          onChange={(e) => updateFormData('language', e.target.value)}
          label="Lingua preferita"
        >
          <MenuItem value="it-IT">Italiano</MenuItem>
          <MenuItem value="en-US">English</MenuItem>
          <MenuItem value="es-ES">Español</MenuItem>
          <MenuItem value="fr-FR">Français</MenuItem>
        </Select>
      </FormControl>

      <Typography 
        variant="body1" 
        sx={{ 
          color: visualTheme.text_colors?.secondary || '#6c757d',
          mt: 4
        }}
      >
        Potrai modificare queste impostazioni in qualsiasi momento dalle impostazioni del tuo profilo.
      </Typography>
    </Box>
  );

  // NUOVO COMPONENTE: Selezione Agente
  const AgentSelectionStep = () => (
    <Box>
      <Typography 
        variant="h5" 
        gutterBottom
        sx={{ 
          color: visualTheme.text_colors?.primary || '#212529',
          fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
          mb: 3
        }}
      >
        Scegli il tuo Agente Principale
      </Typography>

      <Typography 
        variant="body1" 
        sx={{ 
          color: visualTheme.text_colors?.secondary || '#6c757d',
          mb: 4
        }}
      >
        Seleziona l'agente che ti accompagnerà nel tuo viaggio. Potrai aggiungerne altri in seguito.
      </Typography>

      {/* Loading degli agenti */}
      {loadingAgents && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2, color: visualTheme.text_colors?.secondary }}>
            Caricamento agenti disponibili...
          </Typography>
        </Box>
      )}

      {/* Errore nel caricamento */}
      {agentsError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {agentsError}
        </Alert>
      )}

      {/* Lista degli agenti */}
      {!loadingAgents && availableAgents.length > 0 && (
        <RadioGroup
          value={formData.selectedAgentId}
          onChange={(e) => updateFormData('selectedAgentId', e.target.value)}
        >
          <Grid container spacing={2}>
            {availableAgents.map((agent) => (
              <Grid item xs={12} sm={6} md={4} key={agent.agent_id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: formData.selectedAgentId === agent.agent_id 
                      ? `2px solid ${visualTheme.secondary_colors?.accent || '#0d6efd'}` 
                      : '1px solid #e0e0e0',
                    '&:hover': {
                      backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s ease-in-out'
                    },
                    backgroundColor: formData.selectedAgentId === agent.agent_id 
                      ? visualTheme.primary_colors?.background || '#f8f9fa'
                      : 'white'
                  }}
                  onClick={() => updateFormData('selectedAgentId', agent.agent_id)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar 
                        src={agent.ux_avatar_url}
                        sx={{ 
                          width: 48, 
                          height: 48, 
                          mr: 2,
                          backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd'
                        }}
                      >
                        {agent.ux_name ? agent.ux_name.charAt(0) : <SmartToyIcon />}
                      </Avatar>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: visualTheme.text_colors?.primary || '#212529',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {agent.ux_name || 'Agente Senza Nome'}
                        </Typography>
                        
                        {agent.is_main_agent && (
                          <Chip 
                            label="Consigliato" 
                            size="small" 
                            color="primary" 
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </Box>
                      
                      <FormControlLabel
                        value={agent.agent_id}
                        control={<Radio />}
                        label=""
                        sx={{ m: 0 }}
                      />
                    </Box>

                    {agent.ux_description && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: visualTheme.text_colors?.secondary || '#6c757d',
                          fontSize: '0.875rem',
                          lineHeight: 1.4
                        }}
                      >
                        {agent.ux_description}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </RadioGroup>
      )}

      {/* Nessun agente disponibile */}
      {!loadingAgents && !agentsError && availableAgents.length === 0 && (
        <Card sx={{ backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SmartToyIcon sx={{ fontSize: 48, color: visualTheme.text_colors?.secondary, mb: 2 }} />
            <Typography variant="h6" sx={{ color: visualTheme.text_colors?.primary, mb: 1 }}>
              Nessun agente disponibile
            </Typography>
            <Typography variant="body2" sx={{ color: visualTheme.text_colors?.secondary }}>
              Al momento non ci sono agenti configurati per la tua organizzazione.
              Potrai aggiungerne in seguito dalla dashboard.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Opzione per saltare la selezione */}
      {!loadingAgents && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={() => updateFormData('selectedAgentId', '')}
            sx={{ 
              color: visualTheme.text_colors?.secondary || '#6c757d',
              textDecoration: 'underline'
            }}
          >
            Salta questo step (potrai scegliere un agente in seguito)
          </Button>
        </Box>
      )}
    </Box>
  );

  const CompletionStep = () => (
    <Box sx={{ textAlign: 'center' }}>
      <CheckCircleIcon 
        sx={{ 
          fontSize: 80, 
          color: visualTheme.secondary_colors?.accent || '#28a745',
          mb: 3
        }} 
      />
      
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          color: visualTheme.text_colors?.primary || '#212529',
          fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
          mb: 3
        }}
      >
        Perfetto, {formData.displayName}!
      </Typography>

      <Typography 
        variant="h6" 
        sx={{ 
          color: visualTheme.text_colors?.secondary || '#6c757d',
          mb: 4,
          maxWidth: '500px',
          mx: 'auto'
        }}
      >
        Il tuo profilo è stato configurato con successo. Sei pronto/a per iniziare a 
        esplorare tutte le funzionalità di {brandName || config.project_name}.
      </Typography>

      {/* Mostra l'agente selezionato */}
      {formData.selectedAgentId && (
        <Card 
          sx={{ 
            maxWidth: 400, 
            mx: 'auto',
            mb: 4,
            backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
            borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
          }}
        >
          <CardContent>
            <Typography 
              variant="h6" 
              sx={{ 
                color: visualTheme.secondary_colors?.accent || '#0d6efd',
                mb: 2
              }}
            >
              Il tuo Agente Principale:
            </Typography>
            {(() => {
              const selectedAgent = availableAgents.find(a => a.agent_id === formData.selectedAgentId);
              return selectedAgent ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Avatar 
                    src={selectedAgent.ux_avatar_url}
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      mr: 2,
                      backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd'
                    }}
                  >
                    {selectedAgent.ux_name?.charAt(0) || <SmartToyIcon />}
                  </Avatar>
                  <Typography variant="body1" sx={{ color: visualTheme.text_colors?.primary }}>
                    {selectedAgent.ux_name || 'Agente Selezionato'}
                  </Typography>
                </Box>
              ) : null;
            })()}
          </CardContent>
        </Card>
      )}

      <Card 
        sx={{ 
          maxWidth: 400, 
          mx: 'auto',
          backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa',
          borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
        }}
      >
        <CardContent>
          <Typography 
            variant="h6" 
            sx={{ 
              color: visualTheme.secondary_colors?.accent || '#0d6efd',
              mb: 2
            }}
          >
            I tuoi prossimi passi:
          </Typography>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Esplora la dashboard principale
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Inizia una conversazione con il tuo agente
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: visualTheme.text_colors?.secondary }}>
              • Crea il tuo primo contenuto
            </Typography>
            <Typography variant="body2" sx={{ color: visualTheme.text_colors?.secondary }}>
              • Scopri tutte le funzionalità disponibili
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0: return <WelcomeStep />;
      case 1: return <ProfileStep />;
      case 2: return <PreferencesStep />;
      case 3: return <AgentSelectionStep />;
      case 4: return <CompletionStep />;
      default: return null;
    }
  };

  return (
    <Container maxWidth="md">
      <Box 
        sx={{ 
          minHeight: '100vh', 
          py: 4,
          // 🎨 Background dal tema
          backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa'
        }}
      >
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4,
            // 🎨 Paper background dal tema
            backgroundColor: visualTheme.primary_colors?.header || '#ffffff',
            borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
          }}
        >
          {/* 🎨 Progress Stepper */}
          <Stepper 
            activeStep={activeStep} 
            sx={{ 
              mb: 4,
              '& .MuiStepIcon-root.Mui-active': {
                color: visualTheme.secondary_colors?.accent || '#0d6efd',
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: visualTheme.secondary_colors?.accent || '#28a745',
              }
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{
                  '& .MuiStepLabel-label': {
                    color: visualTheme.text_colors?.secondary || '#6c757d',
                    fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif'
                  }
                }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* 🎨 Progress bar */}
          <LinearProgress 
            variant="determinate" 
            value={(activeStep / (steps.length - 1)) * 100} 
            sx={{ 
              mb: 4,
              '& .MuiLinearProgress-bar': {
                backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd'
              }
            }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step Content */}
          <Box sx={{ minHeight: '400px', mb: 4 }}>
            {renderStepContent(activeStep)}
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ 
                color: visualTheme.text_colors?.secondary || '#6c757d',
                fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif'
              }}
            >
              Indietro
            </Button>

            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleComplete}
                  disabled={loading}
                  sx={{ 
                    backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                    borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
                    fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
                    '&:hover': {
                      backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                      opacity: 0.9
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    getText('button_finish_onboarding', 'Inizia il tuo Viaggio')
                  )}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{ 
                    backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                    borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
                    fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
                    '&:hover': {
                      backgroundColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                      opacity: 0.9
                    }
                  }}
                >
                  {getText('button_next', 'Avanti')}
                </Button>
              )}
            </Box>
          </Box>

          {/* 🎨 Debug info in development */}
          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="caption" display="block">
                <strong>Onboarding Debug:</strong><br/>
                Step: {activeStep + 1}/{steps.length}<br/>
                User: {user?.email}<br/>
                Display Name: {formData.displayName || 'Not set'}<br/>
                Selected Agent: {formData.selectedAgentId || 'None'}<br/>
                Available Agents: {availableAgents.length}<br/>
                Theme: {visualTheme.secondary_colors?.accent}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Onboarding;